import React, { useMemo, useState } from 'react';
import { type GetServerSideProps } from 'next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { faPenToSquare } from '@fortawesome/free-regular-svg-icons';
import styles from '../../styles/Cart.module.css';
import {
  type SubscriptionsResponse,
  type CartPageData,
  type Locale,
  type ReviewData,
  type SubscriptionData,
  type BoxesResponse,
  type CartPageResponse,
  type ReviewsResponse,
} from '../../lib/types';
import api from '../../lib/utils/api';
import { mapSubscriptionData } from '../../lib/utils/mappers/map-subscription-data';
import { goToCheckout } from '../../store/actions/cart';
import cmsEndpoints from '../../lib/utils/cmsEndpoints';
import { PageHead } from '../../components/PageHead';
import { clickedCTA } from '../../store/actions/events';
import { ctaClickSources } from '../../lib/constants';
import { mapCartPageData } from '../../lib/utils/mappers/map-cart-page-data';
import { mapReviewData } from '../../lib/utils/mappers/map-review-data';
import { type RootState } from '../../store';
import { CartEmptyState } from '../../components/Cart/CartEmptyState';
import { CartTableDetailsCell } from '../../components/Cart/CartTableDetailsCell';
import { CartTablePriceCell } from '../../components/Cart/CartTablePriceCell';
import { EditCartDrawer } from '../../components/Cart/EditCartModal';

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );

  const locale = context.locale as Locale;

  const [pageData, reviewsData, subscriptionsData, boxesData] =
    await Promise.all([
      api.get<CartPageResponse>(cmsEndpoints.cartPage()),
      api.get<ReviewsResponse>(cmsEndpoints.reviews()),
      api.get<SubscriptionsResponse>(cmsEndpoints.subscriptions()),
      api.get<BoxesResponse>(cmsEndpoints.boxes()),
    ]);

  if (!pageData || !reviewsData || !subscriptionsData || !boxesData) {
    return {
      notFound: true,
    };
  }

  const page: CartPageData = mapCartPageData(
    pageData.data.data.attributes,
    locale
  );

  const reviews: ReviewData[] = (reviewsData.data.data ?? []).map((review) =>
    mapReviewData(review, locale)
  );

  const subscriptions: SubscriptionData[] =
    subscriptionsData.data.data.attributes.options.map((subscription) =>
      mapSubscriptionData(subscription, locale)
    );

  return {
    props: {
      page,
      reviews,
      subscriptions,
    },
  };
};

type CartProps = {
  readonly page: CartPageData;
  readonly subscriptions: SubscriptionData[];
};

function Cart(props: CartProps) {
  const router = useRouter();
  const dispatch = useDispatch();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

  const selectCart = (state: RootState) => state.cart;
  const cart = useSelector(selectCart);

  const isEmpty = useMemo(() => {
    return cart?.data?.items?.length === 0 || !cart?.data?.items;
  }, [cart]);

  const { page, subscriptions } = props;

  let cartTotal = 0;

  if (!isEmpty) {
    for (const cartItem of cart.data!.items) {
      cartTotal += Number.parseFloat(cartItem.price);
    }
  }

  const handleSubmit = () => {
    setIsLoadingCheckout(true);
    /* FIXME: This is very bad solution.
    he redirrect is done through the state,
    and there is no easy way to see that it was done.
    This timeout is how it was solved originally,
    and I didn't have time to change it in the rewrite. */
    setTimeout(() => {
      setIsLoadingCheckout(false);
    }, 1500);
    dispatch(
      clickedCTA({
        name: ctaClickSources.cartPage.nonEmptyCart.name,
        url: router.asPath,
      })
    );
    dispatch(goToCheckout());
  };

  const hanldeOpenEditModal = () => {
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
  };

  const handleGoBackToBoxes = () => {
    router.push('../our-boxes').catch(() => {
      console.error('Failed to navigate');
    });
  };

  return (
    <>
      <PageHead title={page.title} />

      <Container className="py-4">
        {isEmpty && (
          <CartEmptyState
            empty={page.empty}
            discoverButton={page.discoverButton}
            onDiscoverClick={handleGoBackToBoxes}
          />
        )}

        {!isEmpty && cart.data && (
          <>
            <EditCartDrawer
              isOpen={editModalOpen}
              editFormBoxTitle={page.editFormBoxTitle}
              editFormSubscriptionTitle={page.editFormSubscriptionTitle}
              subscriptions={subscriptions}
              onClose={() => {
                handleCloseEditModal();
              }}
            />
            <Row className={`${styles.itemTableHeader} py-4 d-none d-md-flex`}>
              <Col>{page.tablePicture}</Col>
              <Col>{page.tableDetails}</Col>
              <Col>{page.tablePrice}</Col>
              <Col md={1}>{page.tableEdit}</Col>
              <hr className={styles.itemTableHairline} />
            </Row>
            <Row
              className={`${styles.itemTableHeader} py-5 d-block d-md-none`}
            />
            {cart.data.items.map((cartItem) => (
              <Row key={cartItem.id} className="align-items-center">
                <Col className="text-center">
                  <img
                    src={cartItem.thumbnail}
                    className={styles.itemTableImage}
                  />
                </Col>
                <Col>
                  <CartTableDetailsCell
                    name={cartItem.name}
                    subscription={cartItem.subscription}
                    subscriptionFrequencyMonthly={
                      page.subscriptionFrequencyMonthly
                    }
                    subscriptionFrequencyPrepaid={
                      page.subscriptionFrequencyPrepaid
                    }
                    subscriptionLengthMonthly={page.subscriptionLengthMonthly}
                    subscriptionLengthSemiYearly={
                      page.subscriptionLengthSemiYearly
                    }
                    subscriptionLengthYearly={page.subscriptionLengthYearly}
                  />
                </Col>
                <Col>
                  <CartTablePriceCell
                    price={cartItem.price}
                    subscription={cartItem.subscription}
                  />
                </Col>
                <Col xs={12} md={1}>
                  <a
                    className="clickable float-end float-md-none"
                    onClick={() => {
                      hanldeOpenEditModal();
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faPenToSquare}
                      className="text-success-green"
                      size="xl"
                    />
                  </a>
                </Col>

                <hr
                  className={`${styles.itemTableHairline} d-block d-md-none`}
                />
              </Row>
            ))}
            <Row>
              <Col xs={0} md={6} lg={7} xl={8} />
              <Col xs={12} md={6} lg={5} xl={4}>
                <a
                  href="https://www.trustpilot.com/review/tribu-box.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src="/images/trustpilot.png" className="mb-3" />
                </a>
                <p className="h5 d-flex py-2">
                  <span className="flex-fill">{page.priceSubtotal}</span>
                  <span>€{cartTotal}</span>
                </p>
                <p className="h5 fw-normal py-2">{page.priceNotes}</p>
                <Button
                  disabled={isLoadingCheckout}
                  className="w-100 mt-4"
                  size="lg"
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {isLoadingCheckout ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    page.submitButton
                  )}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </>
  );
}

export default Cart;
